import React, {useCallback, useEffect, useRef, useState} from 'react';
import HeatedMap from './HeatedMap';
import {Box} from '@mui/material';
import './StylesHeatedMap.scss';
import ReportPanel from './ReportPanel';
import {
  useConceptExplorer,
  useSaveSearchCriteria,
} from '../../../../hooks/conceptExplorere/useConceptExplorer';
import {t} from 'i18next';
import {ToastType} from '../../../../shared/constants';
import ApplyKeywordModal from './ApplyKeywordModal';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {IChild} from './TypesHeatMap';
import {
  initialToast,
  ReportNotificationType,
  ToastNotificationType,
} from '../ConceptExplorer';
import {IConceptExplorer} from '../../../../api/types/conceptExplorer.type';
import ArrWaitToCompleteModalWrapper from '../../standard-reports/component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import {useSearchParams} from 'react-router-dom';
import {setRenderJSONPayload} from '../../../../redux/slice/comExplorerSlice';
import {useDispatch, useSelector} from 'react-redux';
import {isEmpty} from 'lodash';
interface IHeatedMapAndPanelProps {
  reqData: ReportNotificationType | null;
  cartItems: IChild[];
  setCartItems: React.Dispatch<React.SetStateAction<IChild[]>>;
  toastNotification: ToastNotificationType;
  setToastNotification: React.Dispatch<
    React.SetStateAction<ToastNotificationType>
  >;
  currentLevelIndex?: number;
  setCurrentLevelIndex?: React.Dispatch<React.SetStateAction<number>>;
  selectedCluster?: {[key: number]: string | null};
  setSelectedCluster?: React.Dispatch<
    React.SetStateAction<{[key: number]: string | null}>
  >;
}

const initialClusterSelection = {
  0: null,
  1: null,
  2: null,
};

export const allLevels: string[] = ['1st', '2nd', '3rd'];

const HeatedMapAndPanel = ({
  reqData,
  cartItems,
  setCartItems,
  toastNotification,
  setToastNotification,
  currentLevelIndex: externalCurrentLevelIndex,
  setCurrentLevelIndex: externalSetCurrentLevelIndex,
  selectedCluster: externalSelectedCluster,
  setSelectedCluster: externalSetSelectedCluster,
}: IHeatedMapAndPanelProps) => {
  // Use external state if provided, otherwise use internal state
  const {
    keyword: storedKeyWord,
    currentLevelIndex: storedCurrentLevelIndex,
    ...restPayload
  } = useSelector((state: any) => state.comExplorer?.renderJSONPayload || {});

  const [internalSelectedCluster, setInternalSelectedCluster] = useState<{
    [key: number]: string | null;
  }>(initialClusterSelection);
  const [internalCurrentLevelIndex, setInternalCurrentLevelIndex] =
    useState<number>(storedCurrentLevelIndex ?? 0);

  const selectedCluster = externalSelectedCluster ?? internalSelectedCluster;
  const setSelectedCluster =
    externalSetSelectedCluster ?? setInternalSelectedCluster;
  const currentLevelIndex =
    externalCurrentLevelIndex ?? internalCurrentLevelIndex;
  const setCurrentLevelIndex =
    externalSetCurrentLevelIndex ?? setInternalCurrentLevelIndex;
  const [isApplyingKeyword, setIsApplyingKeyword] = useState<boolean>(false);
  const [clustersCount, setClustersCount] = useState({
    id: 1,
    name: 'Top 30',
    value: 30,
  });
  const [keyword, setKeyword] = React.useState(storedKeyWord || '');
  const [requestData, setRequestData] = useState<{
    notificationId: string | number | null;
    reportId: string | number | null;
    searchId: number | string;
  }>({
    notificationId: null,
    reportId: null,
    searchId: 'undefined',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const reportIdFromUrl = searchParams.get('ReportID');

  const [payload, setPayload] = useState<IConceptExplorer>(
    !isEmpty(restPayload)
      ? restPayload
      : {
          ReportId: null, //Number(searchParams.get('ReportID')) || null, // update when notification completed
          ClusterId: '0',
          SearchId: requestData.searchId,
          ActionType:
            requestData.searchId !== 'undefined'
              ? 'SearchAction'
              : allLevels[currentLevelIndex] + 'Level',
          VizLevel: currentLevelIndex + 1,
          MaxClustersShown: clustersCount.value,
        }
  );
  useEffect(() => {
    if (reportIdFromUrl) {
      setPayload((prev) => ({
        ...prev,
        ReportId: Number(reportIdFromUrl),
      }));
    }
  }, [reportIdFromUrl]);

  const [docsCount, setDocsCount] = useState<{
    totalDocs: number;
    clusterlessTextDocuments: number;
  }>({
    totalDocs: 0,
    clusterlessTextDocuments: 0,
  });

  const previousElement = useRef<Element | null>(null);

  const {
    data = null,
    isFetching,
    isError: errorRender,
    error: conceptError,
  } = useConceptExplorer({
    ...payload,
  });

  const isNewReport = useRef<boolean>(false);

  useEffect(() => {
    if (!reqData) return;
    setRequestData((prev) => ({
      ...prev,
      notificationId: reqData?.notificationId || null,
      reportId: reqData?.reportId || null,
    }));
    isNewReport.current = true;
  }, [reqData]);
  useEffect(() => {
    if (currentLevelIndex === 0) {
      setDocsCount({
        totalDocs: data?.conceptCluster?.totalItems || 0,
        clusterlessTextDocuments:
          data?.conceptCluster?.clusterlessTextDocuments,
      });
    } else {
      setDocsCount((prev) => ({
        ...prev,
        totalDocs:
          data?.conceptCluster?.children.reduce(
            (total: number, child: IChild) => total + child.size,
            0
          ) || 0,
      }));
    }
    setToastNotification(initialToast);
  }, [data]);

  const {mutate: postSaveSearchConceptReport, isError: errorSave} =
    useSaveSearchCriteria();

  const handleClusterChange = (clsCounts: {
    id: number;
    name: string;
    value: number;
  }) => {
    setPayload((prev) => ({
      ...prev,
      MaxClustersShown: clsCounts.value,
    }));
    setClustersCount(clsCounts);
  };
  const handleLevelChange = (index: number) => {
    if (index > currentLevelIndex) {
      const child: IChild = data?.conceptCluster.children.find(
        (child: IChild) => {
          return child.id === selectedCluster[currentLevelIndex];
        }
      );
      if (child.childcount === 0) {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {from: 'client', text: 'noClustersFound'},
        });
        return;
      }
    }
    setPayload((prev) => ({
      ...prev,
      ClusterId:
        index === 0
          ? '0'
          : selectedCluster[
              index < currentLevelIndex ? index - 1 : currentLevelIndex
            ] || '0',
      ActionType:
        requestData.searchId !== 'undefined'
          ? 'SearchAction'
          : allLevels[index] + 'Level',
      VizLevel: index + 1,
    }));
    setCurrentLevelIndex(index);
    setSelectedCluster((prev) => {
      if (index === 0) {
        return {...initialClusterSelection};
      } else {
        if (index < currentLevelIndex) {
          return {...prev, [index]: null, [currentLevelIndex]: null};
        }
        return prev;
      }
    });
  };
  const dispatch = useDispatch();
  useEffect(() => {
    return () => {
      const currentUrl = `${window.location.pathname}${window.location.search}`;
      if (currentUrl.includes('DataAnalysisReport/CommunicationExplorer')) {
        dispatch(
          setRenderJSONPayload({
            ...payload,
            keyword,
            currentLevelIndex,
            MaxClustersShown: 30,
          })
        );
      }
    };
  }, [payload]);

  useEffect(() => {
    if (errorRender || errorSave) {
      const msg = (conceptError as any)?.response?.data?.detail.includes(
        t('reports.dataAnalysisReport.conceptExplorer.noClustersCheck')
      )
        ? 'noClustersFound'
        : 'errorFetchingData';

      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {from: 'server', text: t(msg)},
      });
    }
  }, [errorRender, errorSave, conceptError]);

  useEffect(() => {
    if (!isApplyingKeyword && previousElement.current) {
      (previousElement.current as HTMLElement).focus();
      previousElement.current = null;
    }
  }, [isApplyingKeyword]);

  const handleReset = useCallback(() => {
    setSelectedCluster(initialClusterSelection);
    setCurrentLevelIndex(0);
    setClustersCount({id: 1, name: 'Top 30', value: 30});
    setRequestData((prev) => ({...prev, searchId: 'undefined'}));
    setKeyword('');
    setPayload({
      ReportId:
        requestData?.reportId || Number(searchParams.get('ReportID')) || null,
      ClusterId: '0',
      SearchId: 'undefined',
      ActionType: allLevels[0] + 'Level',
      VizLevel: 1,
      MaxClustersShown: 30,
    });
    setCartItems([]);
  }, [requestData?.reportId]);

  const handleCreate = () => {
    setIsApplyingKeyword(true);
    previousElement.current = document.activeElement;
  };

  const handleWaitComplete = (reportId: number | string) => {
    let newPayload = {...payload, ReportId: reportId};
    if (isNewReport.current) {
      newPayload = {
        ...newPayload,
        ClusterId: '0',
        SearchId: 'undefined',
        ActionType: allLevels[0] + 'Level',
        VizLevel: 1,
        MaxClustersShown: 30,
      };
      setCurrentLevelIndex(0);
      setCartItems([]);
      setKeyword('');
    } else {
      newPayload = {
        ...newPayload,
        SearchId: requestData.searchId,
      };
    }
    setPayload(newPayload);
    setSearchParams((prev) => {
      prev.set('ReportID', String(reportId));
      return prev;
    });
    isNewReport.current = false;
  };

  const handleKeywordApply = useCallback(() => {
    if (keyword.split(' ').length > 1) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {from: 'client', text: 'keywordSearchError'},
      });
      return;
    }

    const payload = {
      ReportId: reqData?.reportId ?? Number(searchParams.get('ReportID')),
      jsonSearchInput: keyword,
      originalSearchInput: keyword,
      isBasicSearch: true,
      includeThreadedDocuments: true,
      includeFamilyMember: true,
      includeNearDuplicate: true,
      editSavedSearchID: 0,
      searchSessionId: 'string',
    };
    postSaveSearchConceptReport(payload, {
      onSuccess: (data = null) => {
        setRequestData({
          searchId: data?.searchID ?? 'undefined',
          reportId: data?.reportID || null,
          notificationId: data?.notificationId || null,
        });
        setIsApplyingKeyword(false);
        isNewReport.current = false;
      },
      onError: () => {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {from: 'server', text: t('keywordSearchError')},
        });
        return;
      },
    });
  }, [keyword, currentLevelIndex]);

  return (
    <>
      <ArrLoadingSpinner show={isFetching} overlay={true} isOpen={true} />
      {requestData?.notificationId && requestData?.reportId && (
        <ArrWaitToCompleteModalWrapper
          notificationId={Number(requestData.notificationId)}
          reportId={Number(requestData.reportId)}
          onComplete={(reportId) => handleWaitComplete(reportId)}
        />
      )}
      <ApplyKeywordModal
        open={isApplyingKeyword}
        setOpen={setIsApplyingKeyword}
        keyword={keyword}
        setKeyword={setKeyword}
        handleApply={handleKeywordApply}
      />
      <Box className="heated-map-container">
        <ReportPanel
          handleClusterChange={handleClusterChange}
          clustersCount={clustersCount}
          selectedCluster={selectedCluster}
          currentLevelIndex={currentLevelIndex}
          handleLevelChange={handleLevelChange}
          docsCount={docsCount}
          isLoading={isFetching}
          handleReset={handleReset}
          handleCreate={handleCreate}
          keyword={keyword}
          isNoClustersFound={
            !data && toastNotification.message.text === 'noClustersFound'
          }
        />
        {data ? (
          <HeatedMap
            data={data?.conceptCluster}
            selectedCluster={selectedCluster}
            setSelectedCluster={setSelectedCluster}
            searchId={payload.SearchId}
            cartItems={cartItems}
            setCartItems={setCartItems}
            currentLevelIndex={currentLevelIndex}
          />
        ) : null}
      </Box>
    </>
  );
};

export default HeatedMapAndPanel;
